import React from "react";
import { Link } from "react-router-dom";
import { Home, School, ArrowRight, CheckCircle2 } from "lucide-react";
import { SHOW_PAYMENT } from "@/lib/paymentVisibility";

/**
 * The fork that sits directly under the hero.
 *
 * A parent and a principal are two different sales, not two skins on one page,
 * so each side routes to its own path with its own proof, pricing and CTA.
 * Families stay on "/" (most traffic); schools go to the /schools journey.
 */
const paths = [
  {
    testid: "fork-families",
    to: "/families",
    icon: Home,
    circle: "hv-icon-circle-coral",
    badge: "For Families",
    badgeClass: "hv-badge hv-badge-coral",
    title: "One child, tonight.",
    titleAccent: "text-hv-coral",
    blurb: "Start in two minutes with a card. Cancel whenever you like.",
    points: [SHOW_PAYMENT ? "$10 / month or $100 / year" : "Start with a free story", "Watch a sample story free", "Family plan for siblings"],
    cta: "Explore for families",
  },
  {
    testid: "fork-schools",
    to: "/schools",
    icon: School,
    circle: "hv-icon-circle",
    badge: "For Schools",
    badgeClass: "hv-badge",
    title: "A whole class, one term.",
    titleAccent: "text-hv-teal",
    blurb: "Run a free pilot with one classroom before anyone signs anything.",
    points: ["Per-student annual licence", "Teacher accounts + bulk roster import", "Plain-language trust one-pager"],
    cta: "Explore for schools",
  },
];

export default function PersonaFork() {
  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 py-12" data-testid="persona-fork">
      <div className="max-w-2xl">
        <span className="hv-badge">Two ways in</span>
        <h2 className="hv-heading mt-4 text-3xl sm:text-4xl">
          <span className="text-hv-navy">Bringing this to your home,</span>{" "}
          <span className="text-hv-teal">or your school?</span>
        </h2>
        <p className="mt-3 text-hv-slate leading-relaxed">
          Both start the same way — with a story. What comes next is different.
        </p>
      </div>

      <div className="mt-10 grid md:grid-cols-2 gap-5 items-stretch">
        {paths.map((p) => {
          const Icon = p.icon;
          return (
            <Link
              key={p.testid}
              to={p.to}
              data-testid={p.testid}
              className="hv-card p-8 flex flex-col group transition hover:-translate-y-1 hover:shadow-lg hv-focus-ring"
            >
              <div className="flex items-center gap-3">
                <div className={`${p.circle} w-12 h-12 rounded-full flex items-center justify-center shrink-0`}>
                  <Icon size={22} strokeWidth={2.5} />
                </div>
                <span className={`${p.badgeClass} self-start`}>{p.badge}</span>
              </div>

              <h3 className={`hv-heading mt-5 text-2xl text-hv-navy`}>
                <span className={p.titleAccent}>{p.title}</span>
              </h3>
              <p className="mt-2 text-hv-slate leading-relaxed">{p.blurb}</p>

              <ul className="mt-6 space-y-2 border-t border-[#E5DFC8] pt-5">
                {p.points.map((pt, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-hv-navy">
                    <CheckCircle2 size={16} className="text-hv-teal mt-0.5 shrink-0" strokeWidth={2.5} />
                    <span>{pt}</span>
                  </li>
                ))}
              </ul>

              <span className="mt-7 inline-flex items-center gap-2 font-bold text-hv-teal group-hover:gap-3 transition-all">
                {p.cta} <ArrowRight size={18} strokeWidth={2.5} />
              </span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
